import Invoice from '@spike/invoice-model';
import { getInvoiceThunk, InvoicesStatus } from '@spike/invoices-action';
import { PaymentsStatus, sendThunk } from '@spike/payments-action';
import { SingleSaleDetail } from '@spike/single-sale-model';
import Button from 'components/UI/V3/Button/Button';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import Spinner from 'components/UI/V3/Spinner';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import SaleDetailBody from './Body/SaleDetailBody';
import DeleteSaleConfirm from './DeleteConfirm/DeleteSaleConfirm';
import styles from './SaleDetailDrawer.module.scss';
import SaleDetailFooter from './Footer/SaleDetailFooter';
import SaleDetailHeader from './Header/SaleDetailHeader';
import SendInvoiceModal from './SendInvoice/SendInvoiceModal';

interface SaleDetailDrawerProps {
    sale?: SingleSaleDetail;
    loading: boolean;
    deleting: boolean;
    timeZone: string;
    onClose: () => void;
    onDelete: () => void;
    onCollectPayment: () => void;
    onEdit: () => void;
}

type InvoicePdfAction = 'view';

export const SaleDetailDrawer: FunctionComponent<SaleDetailDrawerProps> = ({
    sale,
    loading,
    deleting,
    timeZone,
    onClose,
    onDelete,
    onCollectPayment,
    onEdit
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const invoice = useSelector<RootState, Invoice | undefined>(state => state.invoices.invoice);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const [confirmDelete, setConfirmDelete] = useState(false);
    const [sendInvoiceModalOpen, setSendInvoiceModalOpen] = useState(false);
    const [sendingInvoice, setSendingInvoice] = useState(false);
    const [pendingPdfAction, setPendingPdfAction] = useState<InvoicePdfAction | null>(null);
    const status = sale?.status as string | undefined;
    const isIncomplete = status === 'incomplete';
    const isUnpaid = status === 'unpaid';
    const showInvoiceActions = status === 'completed' || status === 'paid';
    const pdfActionLoading = pendingPdfAction !== null;
    const hasPartialPayment = Number(sale?.totals.balanceDue ?? 0) < Number(sale?.totals.total ?? 0);
    const canEdit = isIncomplete || (isUnpaid && !hasPartialPayment);

    const saleDate = useMemo(
        () => sale?.issuedAt.clone().tz(timeZone).format('ddd, MMM DD, YYYY, h:mm a'),
        [sale, timeZone]
    );

    const confirmDeleteHandler = () => {
        onDelete();
        setConfirmDelete(false);
    };

    const executePdfAction = (pdfUrl: string) => {
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    };

    const invoiceId = sale?.invoice.id;

    useEffect(() => {
        if (!invoiceId) {
            return;
        }

        dispatch(getInvoiceThunk(apiClientWrapper, invoiceId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceId]);

    const loadInvoicePdfHandler = (action: InvoicePdfAction) => {
        if (!invoiceId) {
            return;
        }

        setPendingPdfAction(action);
        dispatch(getInvoiceThunk(apiClientWrapper, invoiceId));
    };

    const openSendInvoiceModalHandler = () => {
        if (!invoiceId) {
            return;
        }

        setSendInvoiceModalOpen(true);
    };

    const sendInvoiceHandler = (email: string) => {
        if (!invoiceId) {
            return;
        }

        setSendingInvoice(true);
        dispatch(sendThunk(apiClientWrapper, invoiceId, true, false, false, email));
    };

    useEffect(() => {
        if (paymentsStatus === PaymentsStatus.SendSuccess) {
            setSendingInvoice(false);
            setSendInvoiceModalOpen(false);
        }

        if (paymentsStatus === PaymentsStatus.Error) {
            setSendingInvoice(false);
        }
    }, [paymentsStatus]);

    useEffect(() => {
        if (!pendingPdfAction || invoicesStatus !== InvoicesStatus.GetInvoiceSuccess) {
            return;
        }

        if (invoice?.id === invoiceId && invoice!.pdfUrl) {
            executePdfAction(invoice!.pdfUrl);
            setPendingPdfAction(null);
        }
    }, [invoice, invoiceId, invoicesStatus, pendingPdfAction]);

    useEffect(() => {
        if (invoicesStatus === InvoicesStatus.Error) {
            setPendingPdfAction(null);
        }
    }, [invoicesStatus]);

    return (
        <>
            <Drawer
                zIndex={10020}
                onClose={onClose}
            >
                <div className={styles.root}>
                    {loading && (
                        <div className={styles.loading}>
                            <Spinner size={36} />
                        </div>
                    )}

                    {!loading && !sale && <div className={styles.error}>Could not load sale detail.</div>}

                    {!loading && sale && (
                        <>
                            <SaleDetailHeader
                                sale={sale}
                                status={status}
                                saleDate={saleDate}
                                sendingInvoice={sendingInvoice}
                                showInvoiceActions={showInvoiceActions}
                                isIncomplete={isIncomplete}
                                onClose={onClose}
                                onDelete={() => setConfirmDelete(true)}
                                onSendInvoice={openSendInvoiceModalHandler}
                            />

                            <SaleDetailBody
                                sale={sale}
                                invoice={invoice?.id === invoiceId ? invoice : undefined}
                                loadingInvoice={
                                    Boolean(invoiceId) &&
                                    invoice?.id !== invoiceId &&
                                    invoicesStatus !== InvoicesStatus.Error
                                }
                            />

                            {(isUnpaid || isIncomplete) && (
                                <SaleDetailFooter
                                    isIncomplete={isIncomplete}
                                    canEdit={canEdit}
                                    onCollectPayment={onCollectPayment}
                                    onEdit={onEdit}
                                />
                            )}
                        </>
                    )}
                </div>

                {!loading && sale && showInvoiceActions && (
                    <DrawerFooter
                        sticky={false}
                        className={styles.footer}
                    >
                        <Button
                            label="View PDF"
                            variant="outlined"
                            size="large"
                            fullWidth
                            disabled={!invoiceId || pdfActionLoading}
                            isLoading={pdfActionLoading}
                            onClick={() => loadInvoicePdfHandler('view')}
                        />
                    </DrawerFooter>
                )}
            </Drawer>

            {confirmDelete && (
                <DeleteSaleConfirm
                    deleting={deleting}
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={confirmDeleteHandler}
                />
            )}

            {sendInvoiceModalOpen && sale && (
                <SendInvoiceModal
                    email={sale.client?.email}
                    sending={sendingInvoice}
                    onCancel={() => setSendInvoiceModalOpen(false)}
                    onSend={sendInvoiceHandler}
                />
            )}
        </>
    );
};

export default SaleDetailDrawer;
